<script setup lang="ts">
import { defineProps, computed } from 'vue';

import type { PredefinedAltActionOptions } from '@/library/message/enum';
import { PredefinedOptionType, SystemEvent } from '@/library/message/enum';
import type { BaseSystemMessage, AlternativeActionSelectedEvent } from '@/library/message/types';

import ChatMessageWrapper from './ChatMessageWrapper.vue';
import MessageActions from '../MessageActions.vue';
import type { ActionItem } from '../MessageActions.vue';
import { useI18n } from '@/plugins/i18n';

export interface AlternativeActionsMessageProps {
  message: BaseSystemMessage;
  /**
   * The previous message/notice in the stack.
   */
  previous?: unknown;
  /**
   * The next message/notice in the stack.
   */
  next?: unknown;
}

const props = defineProps<AlternativeActionsMessageProps>();

const emit = defineEmits<{
  (e: 'message:input', event: AlternativeActionSelectedEvent): void;
}>();

const { t } = useI18n();

const buildAlternativeActionEvent = (selected: PredefinedAltActionOptions) => {
  const event: AlternativeActionSelectedEvent = {
    message: props.message,
    type: SystemEvent.ALTERNATIVE_ACTION,
    payload: { selected },
  };

  emit('message:input', event);
};

const handleActionSelect = (item: ActionItem) => {
  buildAlternativeActionEvent(item.id as PredefinedAltActionOptions);
};

const connectSupportActions = computed<ActionItem[]>(() => [
  {
    id: PredefinedOptionType.CONNECT_TO_SUPPORT,
    label: String(t('chat.sys.navigation.contact_support')),
    icon: 'MdSupportagent',
  },
]);

const restartChatActions = computed<ActionItem[]>(() => [
  {
    id: PredefinedOptionType.RESTART_CHAT,
    label: String(t('chat.sys.navigation.restart_chat')),
  },
]);

const endChatActions = computed<ActionItem[]>(() => [
  {
    id: PredefinedOptionType.END_CHAT,
    label: String(t('global.end_chat')),
  },
]);
</script>

<template>
  <ChatMessageWrapper :previous="previous" :next="next" :message="message">
    <template #actions>
      <div>
        <span>{{ $t('chat.sys.navigation.connect_here') }}</span>
        <MessageActions :items="connectSupportActions" @select="handleActionSelect" />
        <span>{{ $t('chat.sys.navigation.different_topic') }}</span>
        <span class="restart-text">{{ $t('chat.sys.navigation.restart_here') }}</span>
        <MessageActions :items="restartChatActions" @select="handleActionSelect" />
        <span>{{ $t('chat.sys.navigation.end_chat_here') }}</span>
        <MessageActions :items="endChatActions" @select="handleActionSelect" />
      </div>
    </template>
  </ChatMessageWrapper>
</template>

<style scoped>
.restart-text {
  display: block;
  margin-top: 16px;
}
</style>
